import { useEffect, useState } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { Link } from "react-router-dom";
import { PageHeader, Button, Badge } from "@/components/ui/PageHeader";
import { InputField, TextAreaField, SelectField } from "@/components/ui/FormFields";
import { Modal } from "@/components/ui/Modal";
import { MetroSelect } from "@/components/MetroSelect";
import { useAuth } from "@/context/AuthContext";
import {
  listQuestions,
  createQuestion,
  listQuestionTopics,
  getQuestionsSummary,
  findQuestionMatches,
  type QuestionListItem,
  type QuestionMatch,
  type QuestionTopic,
  type QuestionScope,
  type Delivery,
  type ListQuestionsParams,
} from "@/api/questions";

const SCOPES: QuestionScope[] = ["city", "metro", "state", "identity", "global"];

/** A topic that cannot carry a trade says so in the option itself. */
function topicOptions(topics?: QuestionTopic[]) {
  return (topics ?? []).map((t) => ({
    value: t.code,
    label: t.allowsProviderCategory ? t.name : `${t.name} (no trade)`,
  }));
}

export default function QuestionsLab() {
  const qc = useQueryClient();
  const { isAuthenticated } = useAuth();
  const [askOpen, setAskOpen] = useState(false);
  const [filters, setFilters] = useState<ListQuestionsParams>({ limit: 30 });

  const { data, isLoading, error, refetch } = useQuery({
    queryKey: ["questions", filters],
    queryFn: () => listQuestions(filters),
  });

  // The same filters, without the paging. Proving they agree is the point.
  const { data: summary } = useQuery({
    queryKey: ["questions-summary", filters],
    queryFn: () => getQuestionsSummary({ ...filters, page: undefined, limit: undefined }),
  });

  const { data: topics } = useQuery({
    queryKey: ["question-topics"],
    queryFn: listQuestionTopics,
  });

  const set = (patch: Partial<ListQuestionsParams>) =>
    setFilters((f) => ({ ...f, ...patch, page: 1 }));

  return (
    <div className="space-y-6">
      <PageHeader
        title="Curios — Community Q&A"
        subtitle="Ask people near you who know your world. Topic, audience, and why a question reached you."
        breadcrumbs={[{ label: "Dashboard", href: "/" }, { label: "Curios" }]}
        actions={
          isAuthenticated && (
            <Button size="sm" onClick={() => setAskOpen(true)}>
              Ask a question
            </Button>
          )
        }
      />

      {summary && (
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
          <Stat
            label="Nobody answered"
            value={summary.data.openNeeds}
            hint="Somebody is still waiting"
          />
          <Stat label="Has answers" value={summary.data.withAnswers} hint="None accepted yet" />
          <Stat label="Resolved" value={summary.data.resolved} hint="Asker accepted an answer" />
          <Stat
            label="Median first answer"
            value={
              summary.data.medianFirstAnswerMinutes === null
                ? "—"
                : `${summary.data.medianFirstAnswerMinutes}m`
            }
            hint="Median, not mean"
          />
        </div>
      )}

      <div className="bg-white border border-gray-200 rounded-2xl p-4 space-y-3">
        <div className="grid grid-cols-1 md:grid-cols-4 gap-3">
          <SelectField
            label="Topic"
            value={filters.topic ?? ""}
            onChange={(e) => set({ topic: e.target.value || undefined })}
            placeholder="Any topic"
            options={topicOptions(topics?.data.topics)}
          />

          <SelectField
            label="Audience"
            value={filters.scope ?? ""}
            onChange={(e) => set({ scope: (e.target.value || undefined) as QuestionScope })}
            placeholder="Any audience"
            options={SCOPES.map((s) => ({ value: s, label: s }))}
          />

          <MetroSelect
            placeholder="Any metro"
            value={filters.metro ?? ""}
            onChange={(code) => set({ metro: code || undefined })}
          />

          <SelectField
            label="Sort"
            value={filters.sort ?? ""}
            onChange={(e) =>
              set({ sort: (e.target.value || undefined) as ListQuestionsParams["sort"] })
            }
            placeholder={isAuthenticated ? "Default (relevant)" : "Default (newest)"}
            options={[
              { value: "relevant", label: "relevant" },
              { value: "created_at", label: "created_at" },
              { value: "answer_count", label: "answer_count" },
              { value: "view_count", label: "view_count" },
            ]}
          />
        </div>
        {!isAuthenticated && (
          <p className="text-xs text-gray-500">
            Signed out, so there is no relevance reason to show — the list falls back to newest
            first.
          </p>
        )}
      </div>

      {isLoading ? (
        <p className="text-sm text-gray-500">Loading…</p>
      ) : error ? (
        <div className="bg-red-50 border border-red-200 text-red-700 text-sm px-4 py-3 rounded-xl">
          Failed to load questions.{" "}
          <button onClick={() => refetch()} className="underline font-medium">
            Retry
          </button>
        </div>
      ) : !data || data.data.questions.length === 0 ? (
        <p className="text-sm text-gray-500">No questions match these filters.</p>
      ) : (
        <div className="space-y-3">
          {data.data.questions.map((q) => (
            <QuestionRow key={q.id} q={q} />
          ))}
        </div>
      )}

      <AskModal
        open={askOpen}
        onClose={() => setAskOpen(false)}
        onCreated={() => {
          setAskOpen(false);
          qc.invalidateQueries({ queryKey: ["questions"] });
          qc.invalidateQueries({ queryKey: ["questions-summary"] });
        }}
      />
    </div>
  );
}

function Stat({ label, value, hint }: { label: string; value: string | number; hint: string }) {
  return (
    <div className="bg-white border border-gray-200 rounded-2xl p-4">
      <div className="text-2xl font-semibold text-gray-900">{value}</div>
      <div className="text-sm font-medium text-gray-700">{label}</div>
      <div className="text-xs text-gray-500 mt-0.5">{hint}</div>
    </div>
  );
}

function QuestionRow({ q }: { q: QuestionListItem }) {
  return (
    <Link
      to={`/lab/questions/${q.id}`}
      className="block bg-white border border-gray-200 rounded-2xl p-4 hover:border-primary-300 hover:shadow-md transition-all"
    >
      <div className="flex items-start justify-between gap-3">
        <div className="min-w-0 flex-1">
          <div className="flex items-center gap-2 flex-wrap">
            <h3 className="font-semibold text-gray-900">{q.title}</h3>
            {q.isResolved && <Badge variant="green">resolved</Badge>}
            {q.topic && <Badge variant="blue">{q.topic.name}</Badge>}
            {q.providerCategory && <Badge variant="gray">{q.providerCategory.name}</Badge>}
            <Badge variant="gray">{q.scope}</Badge>
          </div>
          <p className="text-sm text-gray-600 mt-1 line-clamp-2">{q.body}</p>
          <div className="text-xs text-gray-500 mt-2">
            {q.isAnonymous ? "Asked anonymously" : (q.user?.name ?? "—")} · {q.answerCount} answers
            · {q.viewCount} views
            {q.metro && ` · ${q.metro.name}`}
          </div>
          {q.relevance && (
            <div className="text-xs text-primary-700 mt-1">
              {q.relevance.label}{" "}
              <span className="text-gray-400">({q.relevance.reasons.join(", ")})</span>
            </div>
          )}
        </div>
      </div>
    </Link>
  );
}

function AskModal({
  open,
  onClose,
  onCreated,
}: {
  open: boolean;
  onClose: () => void;
  onCreated: () => void;
}) {
  const [title, setTitle] = useState("");
  const [body, setBody] = useState("");
  const [topicCode, setTopicCode] = useState("");
  const [scope, setScope] = useState<QuestionScope>("global");
  const [city, setCity] = useState("");
  const [state, setState] = useState("");
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);
  const [delivery, setDelivery] = useState<Delivery | null>(null);
  const [matches, setMatches] = useState<QuestionMatch[]>([]);

  const { data: topics } = useQuery({
    queryKey: ["question-topics"],
    queryFn: listQuestionTopics,
  });

  // Advisory duplicate check while typing. Debounced, and its failure is
  // silent — it must never get in the way of asking.
  useEffect(() => {
    if (title.trim().length < 3) {
      setMatches([]);
      return;
    }
    const t = setTimeout(() => {
      findQuestionMatches({
        title: title.trim(),
        body: body.trim() || undefined,
        topicCode: topicCode || undefined,
        scope,
        city: city.trim() || undefined,
      })
        .then((r) => setMatches(r.data.matches))
        .catch(() => setMatches([]));
    }, 400);
    return () => clearTimeout(t);
  }, [title, body, topicCode, scope, city]);

  async function submit() {
    setBusy(true);
    setErr(null);
    try {
      const r = await createQuestion({
        title: title.trim(),
        body: body.trim(),
        topicCode: topicCode || undefined,
        scope,
        city: city.trim() || undefined,
        state: state.trim() || undefined,
      });
      setDelivery(r.data.delivery);
      setTitle("");
      setBody("");
      onCreated();
    } catch (e) {
      setErr(e instanceof Error ? e.message : "Failed to post");
    } finally {
      setBusy(false);
    }
  }

  // Backend requires title 10–200, body 20–5000.
  const valid = title.trim().length >= 10 && body.trim().length >= 20;
  const needsCity = scope === "city" || scope === "metro";

  return (
    <Modal
      isOpen={open}
      onClose={onClose}
      title="Ask a question"
      footer={
        <div className="flex justify-end gap-2">
          <Button variant="secondary" size="sm" onClick={onClose}>
            Cancel
          </Button>
          <Button size="sm" onClick={submit} isLoading={busy} disabled={!valid}>
            Post question
          </Button>
        </div>
      }
    >
      <div className="space-y-3">
        {err && (
          <div className="bg-red-50 border border-red-200 text-red-700 text-sm px-3 py-2 rounded">
            {err}
          </div>
        )}
        {delivery && (
          <div className="bg-green-50 border border-green-200 text-green-800 text-sm px-3 py-2 rounded">
            Delivery: <strong>{delivery.status}</strong> · audience {delivery.audienceBand} ·{" "}
            {delivery.scope}
            {delivery.status === "no_audience" && (
              <div className="text-xs mt-1">
                Nobody was reached. That is a recruitment fact, not a bug.
              </div>
            )}
          </div>
        )}

        <InputField
          label="Title"
          placeholder="At least 10 characters"
          value={title}
          onChange={(e) => setTitle(e.target.value)}
          hint={`${title.trim().length}/200`}
        />

        {matches.length > 0 && (
          <div className="bg-amber-50 border border-amber-200 rounded p-3">
            <div className="text-xs font-medium text-amber-900 mb-1">
              Already asked? Advisory only — you can still post.
            </div>
            <ul className="space-y-1">
              {matches.map((m) => (
                <li key={m.id} className="text-xs text-amber-900">
                  <Link to={`/lab/questions/${m.id}`} className="underline">
                    {m.title}
                  </Link>{" "}
                  <span className="text-amber-700">— {m.matchReason}</span>
                </li>
              ))}
            </ul>
          </div>
        )}

        <TextAreaField
          label="Details"
          placeholder="At least 20 characters"
          rows={5}
          value={body}
          onChange={(e) => setBody(e.target.value)}
          hint={`${body.trim().length}/5000`}
        />

        <SelectField
          label="Topic"
          value={topicCode}
          onChange={(e) => setTopicCode(e.target.value)}
          hint="What it is ABOUT. A trade, if any, is a separate fact."
          placeholder="No topic"
          options={topicOptions(topics?.data.topics)}
        />

        <SelectField
          label="Who is it for?"
          value={scope}
          onChange={(e) => setScope(e.target.value as QuestionScope)}
          hint="Stored, not guessed. `identity` reads your profile and ignores anything sent here."
          options={SCOPES.map((s) => ({ value: s, label: s }))}
        />

        {needsCity && (
          <div className="grid grid-cols-2 gap-3">
            <InputField
              label="City"
              placeholder="San Jose"
              value={city}
              onChange={(e) => setCity(e.target.value)}
              hint={scope === "city" ? "Required, with a state" : "Or leave blank to use your profile"}
            />
            <InputField
              label="State"
              placeholder="CA"
              value={state}
              onChange={(e) => setState(e.target.value)}
            />
          </div>
        )}
      </div>
    </Modal>
  );
}
